import React, { FC, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { useShortCodeRefresh } from 'hooks/loyalty/use-short-code-refresh';
import { useRedeemInRestaurantConfig } from 'hooks/use-redeem-in-restaurant-config';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useLocationContext } from 'state/location';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { isMobile } from 'utils/environment';

import { componentWidgetMap } from '../loyalty-in-restaurant-redemption/loyalty-in-restaurant-redemption';
import { useWidgetComponents } from '../loyalty-widget-components';

import { StyledBottomModal, StyledUnauthenticatedDrawer } from './loyalty-cart-drawer.styled';
import { CartListDrawer } from './your-code-drawer/cart-list-drawer';
import { ConfirmationDrawer } from './your-code-drawer/confirmation-drawer';
import { QRCodeDrawer } from './your-code-drawer/qr-code-drawer';
import { InRestaurantScreen, InRestaurantScreenVariants } from './your-code-drawer/types';

export interface ILoyaltyCartDrawerProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

// Used for enabling the close animation of the drawer
const EXIT_ANIMATION_TIMEOUT = 350;

export const LoyaltyCartDrawer: FC<ILoyaltyCartDrawerProps> = ({ isOpen, setIsOpen }) => {
  const { formatMessage } = useIntl();
  const { trackEvent } = useCdpContext();
  const { location } = useLocationContext();
  const { isAuthenticated } = useAuthContext();

  const { inRestaurantRedemptionCart, showConfirmationModal } = useInRestaurantRedemptionContext();
  const { redeemInRestaurantConfig } = useRedeemInRestaurantConfig();

  const widgets = useWidgetComponents(componentWidgetMap, redeemInRestaurantConfig?.widgets);

  const [drawerType, setDrawerType] = useState(
    isAuthenticated()
      ? !inRestaurantRedemptionCart.length
        ? InRestaurantScreen.QR_CODE
        : InRestaurantScreen.CART_LIST
      : InRestaurantScreen.UNAUTHENTICATED
  );

  const drawerConfig: InRestaurantScreenVariants = {
    [InRestaurantScreen.QR_CODE]: {
      title: '',
      component: <QRCodeDrawer setDrawerType={setDrawerType} widgets={widgets} />,
    },
    [InRestaurantScreen.CART_LIST]: {
      title: formatMessage({ id: 'loyaltyScanRewardsAndOffers' }),
      component: <CartListDrawer setIsOpen={setIsOpen} setDrawerType={setDrawerType} />,
    },
    [InRestaurantScreen.UNAUTHENTICATED]: {
      title: '',
      component: <StyledUnauthenticatedDrawer />,
    },
    [InRestaurantScreen.CONFIRMATION]: {
      title: '',
      component: <ConfirmationDrawer setIsOpen={setIsOpen} />,
    },
  };

  useShortCodeRefresh();

  useEffect(() => {
    // If we remove all items from the in-restaurant cart we close the drawer
    if (drawerType === InRestaurantScreen.CART_LIST && !inRestaurantRedemptionCart.length) {
      setIsOpen(false);
    }
  }, [drawerType, inRestaurantRedemptionCart, setIsOpen]);

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        name: CustomEventNames.SHEET,
        type: EventTypes.Other,
        attributes: {
          name: drawerType === InRestaurantScreen.CART_LIST ? 'Your In-Restaurant Cart' : 'Scan',
          path: location.pathname,
        },
      });
    }
  }, [isOpen, trackEvent]);

  useEffect(() => {
    if (showConfirmationModal) {
      setDrawerType(InRestaurantScreen.CONFIRMATION);
    }
  }, [showConfirmationModal]);

  if (!isMobile()) {
    return null;
  }

  return (
    <StyledBottomModal
      open={isOpen}
      title={drawerConfig[drawerType].title}
      onCloseModal={() => {
        trackEvent({
          name: CustomEventNames.BUTTON_CLICK,
          type: EventTypes.Other,
          attributes: {
            name: 'Close',
            path: location.pathname,
          },
        });
        setTimeout(() => {
          setIsOpen(false);
        }, EXIT_ANIMATION_TIMEOUT);
      }}
      hideOnClickOutside
    >
      {drawerConfig[drawerType].component}
    </StyledBottomModal>
  );
};
